import type { ItemSelectorFieldsFragment } from "~/gql/queries/items.generated";
import type { KitDraft } from "~fulfill/product/kits/helpers/kitDraft";
import type { Kit, KitLine } from "~fulfill/product/kits/helpers/kitTypes";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isRecord from "~/helpers/isRecord";

export const KIT_SOURCE_KIND = "kit";
export const KIT_TARGET_KIND = "kit";
export const NEW_KIT_TARGET_KIND = "newKit";
export const KITS_BASE_PATH = "/fulfill/product/kits";
export const KITS_DOCK_PANEL_ID = "products-kits";
export const NEW_KIT_SEED_FILTER = "seedItemIds";

export interface KitContentsCheckpoint {
	addKind: "item" | "kit";
	baseline?: Kit;
	graph?: Record<string, Kit>;
	graphIncomplete?: boolean;
	kitMultiple: number | null;
	selectedItems: ItemSelectorFieldsFragment[];
	selectedKitId: string;
	typedMultiples: Record<string, number | null>;
	uncommittedLines: KitLine[] | null;
}

export interface KitEditorCheckpoint {
	baseline?: Kit;
	contents?: KitContentsCheckpoint;
	draft: KitDraft;
	graph?: Record<string, Kit>;
	graphIncomplete?: boolean;
	stepIndex: number;
}

export interface KitSourceCheckpoint {
	details?: KitContentsCheckpoint;
	dirty?: boolean;
	kitId: string;
	kitName: string;
	searchText?: string;
	tab: string;
	wizard: KitEditorCheckpoint | null;
}

const isKitLine = (value: unknown): boolean =>
	isRecord(value) &&
	typeof value.id === "string" &&
	(value.kind === "item" || value.kind === "kit") &&
	typeof value.refId === "string" &&
	typeof value.multiple === "number";
const isKitDraft = (value: unknown): boolean =>
	isRecord(value) &&
	typeof value.kitId === "string" &&
	typeof value.name === "string" &&
	typeof value.containerCode === "string" &&
	Array.isArray(value.lines) &&
	value.lines.every(isKitLine) &&
	Array.isArray(value.siteIds) &&
	value.siteIds.every((id) => typeof id === "string");
const hasBaseline = (value: Record<string, unknown>): boolean =>
	(value.graphIncomplete === undefined || typeof value.graphIncomplete === "boolean") &&
	(value.baseline === undefined || isKitDraft(value.baseline)) &&
	(value.graph === undefined || (isRecord(value.graph) && Object.values(value.graph).every(isKitDraft)));
const isContentsCheckpoint = (value: unknown): boolean =>
	isRecord(value) &&
	hasBaseline(value) &&
	(value.addKind === "item" || value.addKind === "kit") &&
	Array.isArray(value.selectedItems) &&
	value.selectedItems.every((item) => isRecord(item) && typeof item.itemId === "string") &&
	typeof value.selectedKitId === "string" &&
	(value.kitMultiple === null || typeof value.kitMultiple === "number") &&
	(value.uncommittedLines === null ||
		(Array.isArray(value.uncommittedLines) && value.uncommittedLines.every(isKitLine))) &&
	isRecord(value.typedMultiples) &&
	Object.values(value.typedMultiples).every((multiple) => multiple === null || typeof multiple === "number");

export const isKitSourceCheckpoint = (value: unknown): value is KitSourceCheckpoint =>
	isRecord(value) &&
	typeof value.kitId === "string" &&
	typeof value.kitName === "string" &&
	typeof value.tab === "string" &&
	(value.dirty === undefined || typeof value.dirty === "boolean") &&
	(value.searchText === undefined || typeof value.searchText === "string") &&
	(value.details === undefined || isContentsCheckpoint(value.details)) &&
	(value.wizard === null ||
		(isRecord(value.wizard) &&
			hasBaseline(value.wizard) &&
			isKitDraft(value.wizard.draft) &&
			typeof value.wizard.stepIndex === "number" &&
			Number.isInteger(value.wizard.stepIndex) &&
			value.wizard.stepIndex >= 0 &&
			value.wizard.stepIndex <= 2 &&
			(value.wizard.contents === undefined || isContentsCheckpoint(value.wizard.contents))));

export const newKitSeedFilters = (itemIds: readonly string[]): Record<string, string> | undefined =>
	itemIds.length > 0 ? { [NEW_KIT_SEED_FILTER]: JSON.stringify(itemIds) } : undefined;

export const newKitSeedItemIds = (seedFilter: string | undefined): string[] => {
	try {
		const parsed: unknown = JSON.parse(seedFilter ?? "[]");
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
	} catch {
		return [];
	}
};

export const matchKitReceiverRoute: ReceiverIdentityAdapter["matchesRoute"] = (pathname) => {
	const match = /^\/fulfill\/product\/kits\/([^/?#]+)$/.exec(pathname);
	if (!match || match[1] === "import") return null;
	try {
		const targetId = decodeURIComponent(match[1]);
		return { targetId, targetKind: targetId === "new" ? NEW_KIT_TARGET_KIND : KIT_TARGET_KIND };
	} catch {
		return null;
	}
};

const kitReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${KITS_BASE_PATH}/${encodeURIComponent(targetId)}`,
	matchesRoute: matchKitReceiverRoute
};

export default kitReceiverIdentityAdapter;
